import { ActiveElement, Chart, Interaction, InteractionModeFunction, Plugin } from 'chart.js';
import { chartPalette } from '../shared/chart.utils';
import { BuildCtx, BuiltChart, Sets, axes, baseOptions, fmtNum } from './board-charts';
import { alignAxis, tooltipTime } from './board-sync';
import { ValveRow, uiLang, valveAt, valveLabel } from './board-valves';
import { XY } from './board-data';

/**
 * Charts of the board's valve overview (board-valves.ts):
 *
 * - 'valves.all': a heatmap — one row per room (grouped by floor, labelled "E1 WC"), the board's time axis as
 *   x, the valve opening as the cell colour on a sequential teal scale (the dashboard's valve colour; light →
 *   dark on the light theme and in print, dark → bright on the dark theme; a hatch where nothing is known) with
 *   a small colour legend above the plot. A Chart.js line chart with a linear x axis (the board's window, its
 *   drag / pinch zoom and crosshair) whose cells, row labels and legend the plugin 'valveHeatmap' draws: each
 *   pixel column is the time-weighted mean of the held positions over its time span, so any resolution of
 *   the data draws right. The row labels sit in the left y axis, which takes part in the board's axis-width
 *   sync (capped, labels cut with "…"). Hover / tap: interaction mode 'valveCell' — the tooltip names the
 *   room under the pointer and its position at the time shown.
 * - 'valves.<floor>': one stepped line per room of the floor (distinct colours, legend with the room names,
 *   0–100 %), each a dataset with the room's device id as linkKey (the board's legend selection).
 */

/** Sequential teal ramps (OKLCH, lightness monotonic), 0 % → 100 % in 10 % steps: light theme / print and
 *  dark theme (0 % recedes into the card surface, 100 % stands out). */
const RAMP_LIGHT = ['#e0f6ef', '#bde7da', '#9bd8c5', '#79c9b0', '#56b99b', '#32a887', '#009673', '#008460', '#007150', '#005e40', '#004b31'];
const RAMP_DARK  = ['#20352f', '#1d493e', '#175d4d', '#09725c', '#00876c', '#009b7c', '#1cb08c', '#39c49d', '#63d6b1', '#85e7c4', '#a4fad9'];
/** Colour levels of the cells (2 % steps): 0..LEVELS. */
const LEVELS = 50;
const GAP = 255;

const lutCache = new Map<boolean, string[]>();
/** The LEVELS + 1 cell colours of a theme (interpolated between the ramp's steps). */
function lut(dark: boolean): string[] {
  let l = lutCache.get(dark);
  if (l) return l;
  const ramp = (dark ? RAMP_DARK : RAMP_LIGHT).map(h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)));
  l = [];
  for (let k = 0; k <= LEVELS; k++) {
    const p = (k / LEVELS) * (ramp.length - 1);
    const i = Math.min(ramp.length - 2, Math.floor(p)), f = p - i;
    const c = ramp[i].map((v, j) => Math.round(v + (ramp[i + 1][j] - v) * f));
    l.push(`rgb(${c[0]},${c[1]},${c[2]})`);
  }
  lutCache.set(dark, l);
  return l;
}

/** The heatmap colour of an opening (0..100 %); light theme / print unless `dark`. */
function valveHeatColor(pct: number, dark = false): string {
  return lut(dark)[Math.round(Math.max(0, Math.min(100, pct)) / 100 * LEVELS)];
}

/** The dashboard's valve colour (gauges, the room charts' valve line). */
export const VALVE_COLOR = '#0ca678';

/** Line colours of a floor's rooms (validated categorical order, no set-point orange); light / dark. */
const FLOOR_LIGHT = ['#2a78d6', '#1baf7a', '#4a3aa7', '#eda100', '#e87ba4', '#e34948'];
const FLOOR_DARK  = ['#3987e5', '#199e70', '#9085e9', '#c98500', '#d55181', '#e66767'];

/** Row height (CSS px) and the extra gap between two floors. */
const ROW_H = { desktop: 18, phone: 15, print: 16 };
const FLOOR_GAP = 4;
/** Room above the plot for the colour legend. */
const LEGEND_H = 24;
/** At most this wide (CSS px) the row labels' axis gets (the board's other charts align to it). */
const LABEL_MAX = { desktop: 130, phone: 84, print: 150 };

const pctText = (v: number) => `${fmtNum(v, 0)} %`;

/** The chart of 'valves.all' (floor null) or 'valves.<floor>'. */
export function buildValveChart(floor: string | null, rows: ValveRow[] | null, ctx: BuildCtx): BuiltChart {
  registerValveHeatmap();
  return floor ? floorChart(rows ?? [], ctx) : heatmapChart(rows ?? [], ctx);
}

function pointsIn(rows: { line: XY[] }[], ctx: BuildCtx): number {
  let n = 0;
  for (const r of rows) for (const d of r.line) if (d.y != null && d.x >= ctx.win.from && d.x <= ctx.win.to) n++;
  return n;
}

/** One line per room of a floor (held positions stepped, PWM duty as a line). */
function floorChart(rows: ValveRow[], ctx: BuildCtx): BuiltChart {
  const sets = new Sets(ctx);
  const lang = uiLang();
  const colors = ctx.dark ? FLOOR_DARK : FLOOR_LIGHT;
  // stats as a room chart's valve: the KNX series time-weighted, with the time fully open / closed
  rows.forEach((r, i) => sets.add(r.deviceId, valveLabel(r, lang, true), colors[i % colors.length], r.line, pctText,
    { kind: 'valve', unit: '%', digits: 0, hold: r.stepped, ...(r.series ? { src: { series: r.series, field: 'value', agg: 'time' } } : {}) },
    { stepped: r.stepped ? 'before' : false, tension: 0, spanGaps: false }));
  const ax = axes(ctx);
  const y = { ...ax.pctLeft, max: 100, ticks: { ...ax.pctLeft.ticks, stepSize: 25 } };
  return {
    type: 'line',
    data: { datasets: sets.datasets },
    options: baseOptions(ctx, { y }, sets.fmts),
    series: sets.series,
    points: pointsIn(rows, ctx),
  };
}

/** Options of the plugin 'valveHeatmap' (options.plugins.valveHeatmap). */
interface HeatOpts {
  rows: ValveRow[];
  labels: string[];
  dark: boolean;
  font: number;
  textColor: string;
  hatchColor: string;
  labelMax: number;
  legend: { noData: string };
}

function heatmapChart(rows: ValveRow[], ctx: BuildCtx): BuiltChart {
  const lang = uiLang();
  const t = ctx.t;
  const font = ctx.print ? 12 : ctx.phone ? 10 : 11;
  const textColor = ctx.print ? '#333' : chartPalette(ctx.dark).textColor;
  const labels = rows.map(r => valveLabel(r, lang));
  const heat: HeatOpts = {
    rows, labels, dark: ctx.dark && !ctx.print, font, textColor,
    hatchColor: ctx.dark && !ctx.print ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)',
    labelMax: ctx.print ? LABEL_MAX.print : ctx.phone ? LABEL_MAX.phone : LABEL_MAX.desktop,
    legend: { noData: t('BOARD_VALVES_NO_DATA') },
  };
  // y: a placeholder 0..1 axis — its width is the row labels' (drawn by the plugin), aligned with the board
  const y = {
    type: 'linear', min: 0, max: 1, display: true,
    grid: { display: false, drawTicks: false }, border: { display: false },
    ticks: { display: false },
  };
  const options = baseOptions(ctx, { y }, []);
  options.scales.y.afterFit = (scale: any) => { scale.width = labelAxisWidth(scale.chart, heat); alignAxis(scale); };
  options.layout = { ...options.layout, padding: { ...options.layout.padding, top: LEGEND_H } };
  options.plugins.legend = { display: false };
  options.plugins.valveHeatmap = heat;
  options.interaction = { mode: 'valveCell', intersect: false };
  options.plugins.tooltip.filter = (item: any) => stateOf(item.chart).hover != null;
  options.plugins.tooltip.callbacks = {
    ...options.plugins.tooltip.callbacks,
    label: (item: any) => {
      const i = stateOf(item.chart).hover;
      if (i == null || !rows[i]) return '';
      const ts = tooltipTime([item]);
      const v = ts == null ? null : valveAt(rows[i].line, ts);
      return ` ${labels[i]}: ${v == null ? t('BOARD_VALVES_NO_DATA') : pctText(v)}`
        + (rows[i].likely ? ` · ${t('KNX_VALVE_LIKELY')}` : '');
    },
    labelColor: (item: any) => {
      const i = stateOf(item.chart).hover;
      const ts = tooltipTime([item]);
      const v = i == null || ts == null || !rows[i] ? null : valveAt(rows[i].line, ts);
      const c = v == null ? 'rgba(128,128,128,0.4)' : valveHeatColor(v, heat.dark);
      return { borderColor: c, backgroundColor: c };
    },
  };
  // one invisible point: the tooltip's anchor (Chart.js shows a tooltip for elements only)
  const probe = {
    linkKey: 'cells', label: t('KNX_VALVE'), data: [{ x: ctx.win.from, y: 0.5 }],
    showLine: false, pointRadius: 0, pointHoverRadius: 0, pointHitRadius: 0,
    borderColor: 'transparent', backgroundColor: 'transparent',
  };
  const rowH = ctx.print ? ROW_H.print : ctx.phone ? ROW_H.phone : ROW_H.desktop;
  const xAxis = font + 16;
  return {
    type: 'line',
    data: { datasets: [probe] },
    options,
    series: [],
    points: pointsIn(rows, ctx),
    height: LEGEND_H + rows.length * rowH + floorBreaks(rows) * FLOOR_GAP + xAxis + 8,
  };
}

/** Number of floor changes between consecutive rows. */
function floorBreaks(rows: { floor: string }[]): number {
  let n = 0;
  for (let i = 1; i < rows.length; i++) if (rows[i].floor !== rows[i - 1].floor) n++;
  return n;
}

// ── plugin ───────────────────────────────────────────────────────────────────

interface HeatState {
  opts: HeatOpts | null;
  /** Top of each row and the rows' height (CSS px), from the last layout. */
  tops: number[];
  rowH: number;
  /** The row under the pointer (hover / tap), null: none. */
  hover: number | null;
  /** Colour levels per row and pixel column (GAP: nothing known), for one axis range and plot width. */
  cells: { key: string; levels: Uint8Array[] } | null;
  hatch: CanvasPattern | null;
  hatchKey: string;
}
const states = new WeakMap<Chart, HeatState>();

function stateOf(chart: Chart): HeatState {
  let s = states.get(chart);
  if (!s) { s = { opts: null, tops: [], rowH: 0, hover: null, cells: null, hatch: null, hatchKey: '' }; states.set(chart, s); }
  return s;
}

function heatOpts(chart: Chart): HeatOpts | null {
  const o = (chart.config.options as any)?.plugins?.valveHeatmap;
  return o && Array.isArray(o.rows) ? o as HeatOpts : null;
}

const fontOf = (size: number, bold = false) => `${bold ? '600 ' : ''}${size}px ${(Chart.defaults.font as any).family}`;

/** Width of the row labels' axis: the longest label (capped), plus padding. */
function labelAxisWidth(chart: Chart, o: HeatOpts): number {
  const c = chart.ctx;
  if (!c || !o.labels.length) return 0;
  c.save();
  c.font = fontOf(o.font);
  let w = 0;
  for (const l of o.labels) w = Math.max(w, c.measureText(l).width);
  c.restore();
  return Math.ceil(Math.min(w, o.labelMax)) + 8;
}

/** `text` cut with "…" to `max` px. */
function cut(c: CanvasRenderingContext2D, text: string, max: number): string {
  if (c.measureText(text).width <= max) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (c.measureText(text.slice(0, mid) + '…').width <= max) lo = mid; else hi = mid - 1;
  }
  return text.slice(0, lo).trimEnd() + '…';
}

/** Row layout in the chart area: equal rows, a gap between floors. */
function layoutRows(chart: Chart, st: HeatState, o: HeatOpts): void {
  const ca = chart.chartArea;
  const n = o.rows.length;
  if (!ca || !n) { st.tops = []; st.rowH = 0; return; }
  const breaks = floorBreaks(o.rows);
  st.rowH = Math.max(2, (ca.bottom - ca.top - breaks * FLOOR_GAP) / n);
  st.tops = [];
  let y = ca.top;
  for (let i = 0; i < n; i++) {
    if (i && o.rows[i].floor !== o.rows[i - 1].floor) y += FLOOR_GAP;
    st.tops.push(y);
    y += st.rowH;
  }
}

/** The row at canvas y, null between rows / outside. */
function rowAt(st: HeatState, y: number): number | null {
  for (let i = 0; i < st.tops.length; i++) if (y >= st.tops[i] && y < st.tops[i] + st.rowH) return i;
  return null;
}

/**
 * Colour levels of a row per pixel column [0, w): the time-weighted mean of the held values over the
 * column's time span (a column known for less than half its span: GAP).
 */
function rowLevels(line: XY[], min: number, max: number, w: number): Uint8Array {
  const out = new Uint8Array(w).fill(GAP);
  if (w <= 0 || max <= min) return out;
  const sum = new Float64Array(w), cov = new Float64Array(w);
  const pxPerMs = w / (max - min);
  for (let i = 0; i + 1 < line.length; i++) {
    const v = line[i].y;
    if (v == null || !isFinite(v)) continue;
    const a = Math.max(0, (line[i].x - min) * pxPerMs), b = Math.min(w, (line[i + 1].x - min) * pxPerMs);
    if (b <= a) continue;
    for (let c = Math.floor(a); c < b; c++) {
      const part = Math.min(b, c + 1) - Math.max(a, c);
      sum[c] += v * part;
      cov[c] += part;
    }
  }
  for (let c = 0; c < w; c++) {
    if (cov[c] >= 0.5) out[c] = Math.round(Math.max(0, Math.min(100, sum[c] / cov[c])) / 100 * LEVELS);
  }
  return out;
}

function hatchPattern(chart: Chart, st: HeatState, color: string): CanvasPattern | null {
  if (st.hatch && st.hatchKey === color) return st.hatch;
  const p = document.createElement('canvas');
  p.width = p.height = 6;
  const c = p.getContext('2d');
  if (!c) return null;
  c.strokeStyle = color;
  c.lineWidth = 1;
  c.beginPath();
  c.moveTo(0, 6); c.lineTo(6, 0);
  c.moveTo(-1, 1); c.lineTo(1, -1);
  c.moveTo(5, 7); c.lineTo(7, 5);
  c.stroke();
  st.hatch = chart.ctx.createPattern(p, 'repeat');
  st.hatchKey = color;
  return st.hatch;
}

/** Hover / tap: the row under the pointer (the probe point as the tooltip's element). */
const valveCell: InteractionModeFunction = (chart, e) => {
  const st = stateOf(chart as Chart);
  const ca = chart.chartArea;
  const inside = !!ca && e.x != null && e.y != null && e.x >= ca.left && e.x <= ca.right && e.y >= ca.top && e.y <= ca.bottom;
  st.hover = inside ? rowAt(st, e.y!) : null;
  if (st.hover == null) return [];
  const el = chart.getDatasetMeta(0)?.data?.[0];
  return el ? [{ element: el, datasetIndex: 0, index: 0 } as ActiveElement] : [];
};

export const valveHeatmapPlugin: Plugin = {
  id: 'valveHeatmap',

  afterLayout(chart) {
    const o = heatOpts(chart);
    if (!o) return;
    const st = stateOf(chart);
    if (st.opts !== o) { st.opts = o; st.cells = null; }
    layoutRows(chart, st, o);
  },

  afterEvent(chart, args) {
    const o = heatOpts(chart);
    if (!o || args.event.type !== 'mouseout') return;
    const st = stateOf(chart);
    if (st.hover != null) { st.hover = null; args.changed = true; }
  },

  beforeDatasetsDraw(chart) {
    const o = heatOpts(chart);
    const x: any = chart.scales['x'];
    const ca = chart.chartArea;
    if (!o || !x || !ca) return;
    const st = stateOf(chart);
    const c = chart.ctx;
    const w = Math.max(0, Math.round(ca.right - ca.left));
    const key = `${x.min}|${x.max}|${w}`;
    if (!st.cells || st.cells.key !== key) st.cells = { key, levels: o.rows.map(r => rowLevels(r.line, x.min, x.max, w)) };
    const colors = lut(o.dark);
    const hatch = hatchPattern(chart, st, o.hatchColor);
    const h = st.rowH >= 7 ? st.rowH - 1 : st.rowH;   // a 1 px gap between rows
    c.save();
    c.beginPath();
    c.rect(ca.left, ca.top, ca.right - ca.left, ca.bottom - ca.top);
    c.clip();
    st.cells.levels.forEach((lv, i) => {
      const top = st.tops[i];
      if (top == null) return;
      let s = 0;
      for (let k = 1; k <= w; k++) {
        if (k < w && lv[k] === lv[s]) continue;
        if (lv[s] === GAP) { if (hatch) { c.fillStyle = hatch; c.fillRect(ca.left + s, top, k - s, h); } }
        else { c.fillStyle = colors[lv[s]]; c.fillRect(ca.left + s, top, k - s, h); }
        s = k;
      }
    });
    if (st.hover != null && st.tops[st.hover] != null) {
      c.strokeStyle = o.textColor;
      c.lineWidth = 1;
      c.strokeRect(ca.left + 0.5, st.tops[st.hover] + 0.5, ca.right - ca.left - 1, h - 1);
    }
    c.restore();

    // row labels in the left axis, cut to its width
    const y: any = chart.scales['y'];
    if (y) {
      c.save();
      c.font = fontOf(o.font);
      c.textAlign = 'right';
      c.textBaseline = 'middle';
      const right = y.right - 6;
      const max = Math.max(0, right - y.left - 2);
      o.labels.forEach((label, i) => {
        const top = st.tops[i];
        if (top == null) return;
        c.fillStyle = o.textColor;
        c.fillText(cut(c, label, max), right, top + h / 2);
      });
      c.restore();
    }
    drawLegend(chart, o, colors, hatch);
  },

  afterDestroy(chart) {
    states.delete(chart);
  },
};

/** "▨ keine Daten   0 % ▬▬▬ 100 %" above the plot, right-aligned. */
function drawLegend(chart: Chart, o: HeatOpts, colors: string[], hatch: CanvasPattern | null): void {
  const ca = chart.chartArea;
  const c = chart.ctx;
  const size = Math.max(9, o.font - 1);
  const barW = 84, barH = 8, sw = 14, gap = 5;
  const yMid = ca.top - LEGEND_H / 2 - 1;
  c.save();
  c.font = fontOf(size);
  c.textBaseline = 'middle';
  c.fillStyle = o.textColor;
  const lo = '0 %', hi = '100 %';
  let x = ca.right;
  c.textAlign = 'right';
  c.fillText(hi, x, yMid);
  x -= c.measureText(hi).width + gap + barW;
  const step = barW / colors.length;
  colors.forEach((col, i) => { c.fillStyle = col; c.fillRect(x + i * step, yMid - barH / 2, step + 0.5, barH); });
  c.fillStyle = o.textColor;
  x -= gap;
  c.fillText(lo, x, yMid);
  x -= c.measureText(lo).width + 14;
  c.fillText(o.legend.noData, x, yMid);
  x -= c.measureText(o.legend.noData).width + gap + sw;
  if (x >= ca.left - 40 && hatch) {
    c.fillStyle = hatch;
    c.fillRect(x, yMid - barH / 2, sw, barH);
    c.strokeStyle = o.hatchColor;
    c.strokeRect(x + 0.5, yMid - barH / 2 + 0.5, sw - 1, barH - 1);
  }
  c.restore();
}

let registered = false;
/** Register the plugin and the interaction mode (once). */
export function registerValveHeatmap(): void {
  if (registered) return;
  registered = true;
  (Interaction.modes as any).valveCell = valveCell;
  Chart.register(valveHeatmapPlugin);
}
